import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PageTitle, Panel, Table, Field, TextInput, Select, Btn, AddButton, RowActions, EmptyState, Modal, StatCard } from "../components/ui";
import { useI18n } from "../i18n";
import { CL } from "../api";

export default function ScopePage({ scope }) {
  const { t } = useI18n();
  const key = `scope${scope}`;
  const [rows, setRows] = useState([]);
  const [factors, setFactors] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ factor_id: "", facility_id: "", activity_value: "", period_year: 2025, note: "" });

  const load = () => CL.listActivities(scope).then(setRows).catch(() => {});
  useEffect(() => {
    load();
    CL.listFactors(scope).then(setFactors).catch(() => {});
    CL.listFacilities().then(setFacilities).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const total = useMemo(() => rows.reduce((s, r) => s + (r.emissions || 0), 0), [rows]);
  const selectedFactor = factors.find((f) => f.id === form.factor_id);
  const preview = selectedFactor && form.activity_value ? (Number(form.activity_value) * selectedFactor.factor).toFixed(2) : null;

  const openNew = () => { setEditing(null); setForm({ factor_id: "", facility_id: "", activity_value: "", period_year: 2025, note: "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ factor_id: r.factor_id, facility_id: r.facility_id, activity_value: r.activity_value, period_year: r.period_year, note: r.note || "" }); setOpen(true); };

  const save = async () => {
    const f = factors.find((x) => x.id === form.factor_id);
    if (!f) { toast.error(t("selectFactor")); return; }
    if (!form.activity_value) { toast.error(t("activityValue")); return; }
    const fac = facilities.find((x) => x.id === form.facility_id);
    const payload = {
      scope, category: f.category, factor_id: f.id, unit: f.unit, factor: f.factor,
      facility_id: form.facility_id || "", facility_name: fac ? fac.name : "",
      activity_value: Number(form.activity_value), period_year: Number(form.period_year) || 2025, note: form.note,
    };
    try {
      if (editing) await CL.updateActivity(editing.id, payload);
      else await CL.createActivity(payload);
      setOpen(false); load(); toast.success(t("saved"));
    } catch { toast.error("Error"); }
  };

  const del = async (r) => { if (!window.confirm(t("confirmDelete"))) return; await CL.deleteActivity(r.id); load(); };

  return (
    <div data-testid={`scope-page-${scope}`}>
      <PageTitle title={t(key)} subtitle={t("emissions")} action={<AddButton label={t("add")} onClick={openNew} />} />

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard testId="scope-total" label={`${t(key)} — ${t("total")}`} value={(total / 1000).toFixed(2)} sub={t("tCO2e")} />
        <StatCard testId="scope-count" label="Kayıt" value={rows.length} />
        <StatCard testId="scope-kg" label={t("total")} value={total.toLocaleString()} sub={t("kgco2e")} accent="#2f8f5b" />
      </div>

      <Panel>
        {rows.length === 0 ? <EmptyState text={t("noData")} /> : (
          <Table head={[{ label: t("category") }, { label: t("facility") }, { label: t("activityValue"), right: true }, { label: t("factor"), right: true }, { label: t("emissions"), right: true }, { label: t("period") }, { label: t("actions"), right: true }]}>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`activity-row-${r.id}`} className="hover:bg-[#fafbf6]">
                <td className="px-4 py-3 font-medium text-[#1b2420]">{r.category}</td>
                <td className="px-4 py-3 text-[#4b544c]">{r.facility_name || "—"}</td>
                <td className="px-4 py-3 text-right text-[#4b544c]">{r.activity_value} {r.unit}</td>
                <td className="px-4 py-3 text-right text-[#4b544c]">{r.factor}</td>
                <td className="px-4 py-3 text-right font-medium text-[#14512f]">{r.emissions.toLocaleString()} kg</td>
                <td className="px-4 py-3 text-[#4b544c]">{r.period_year}</td>
                <td className="px-4 py-3"><RowActions onEdit={() => openEdit(r)} onDelete={() => del(r)} /></td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Modal open={open} onClose={() => setOpen(false)} title={`${t(key)} — ${editing ? t("edit") : t("add")}`}
        footer={<><Btn variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Btn><Btn onClick={save} data-testid="activity-save">{t("save")}</Btn></>}>
        <Field label={t("factor")}>
          <Select data-testid="activity-factor" value={form.factor_id} onChange={(e) => setForm({ ...form, factor_id: e.target.value })}>
            <option value="">{t("selectFactor")}</option>
            {factors.map((f) => <option key={f.id} value={f.id}>{f.name} — {f.factor} kg/{f.unit}</option>)}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={`${t("activityValue")}${selectedFactor ? ` (${selectedFactor.unit})` : ""}`}>
            <TextInput type="number" data-testid="activity-value" value={form.activity_value} onChange={(e) => setForm({ ...form, activity_value: e.target.value })} />
          </Field>
          <Field label={t("period")}><TextInput type="number" data-testid="activity-year" value={form.period_year} onChange={(e) => setForm({ ...form, period_year: e.target.value })} /></Field>
        </div>
        <Field label={t("facility")}>
          <Select data-testid="activity-facility" value={form.facility_id} onChange={(e) => setForm({ ...form, facility_id: e.target.value })}>
            <option value="">{t("selectFacility")}</option>
            {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </Select>
        </Field>
        <Field label={t("note")}><TextInput data-testid="activity-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
        {preview && <div className="rounded-lg bg-[#eef6ef] px-4 py-3 text-sm text-[#14512f]" data-testid="emission-preview">≈ {Number(preview).toLocaleString()} {t("kgco2e")}</div>}
      </Modal>
    </div>
  );
}
